import { getArrowHeadGeometry } from '../utils/arrowHead';

// 뷰어 회전 시 종횡비 보정에 쓰는 비균일 스케일.
// 라벨(Annotations.js)에서 쓰는 값과 동일한 규칙을 사용한다.
function getAspectScale(Store) {
  const width = Store.getWidth() || 1;
  const height = Store.getHeight() || 1;
  const aspectRatio = width / height;
  return {
    scaleX: aspectRatio > 1 ? 1 / aspectRatio : 1,
    scaleY: aspectRatio < 1 ? aspectRatio : 1,
  };
}

// 누름 위치(ox,oy)에서 현재 위치(x,y)까지의 변위를
// 화면 정렬 로컬 좌표계의 변위로 변환한다. (localToUser의 역변환)
function userDeltaToLocal(x, y, scaleX, scaleY, theta, ox, oy) {
  const rad = (theta * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const gx = (x - ox) / scaleX;
  const gy = (y - oy) / scaleY;
  // rotate(+theta) 행렬: [[cos, -sin], [sin, cos]]
  return {
    dlx: gx * cos - gy * sin,
    dly: gx * sin + gy * cos,
  };
}

export default function move(x, y, Dispatcher, Store) {
  switch (Store.getMode()) {

    case 'DRAW':
      if (Store.isActivityInProgress()) {
        const last = Store.getLast();
        if (last && last[0] === 'path') {
          const d = last[1].d;
          Dispatcher.dispatch({
            type: 'ANNOTATIONS_UPDATE_LAST',
            update: { d: `${d} L${x} ${y}` },
          });
        }
      }
      break;

    case 'DRAWRECT':
      if (Store.isActivityInProgress()) {
        const last = Store.getLast();
        if (last && last[0] === 'rect') {
          const theta = Store.getRotation() || 0;
          if (!theta) {
            // 회전 없음: 기존 로직(이미지 축 정렬)
            const dx = last[1].originX - x;
            const dy = last[1].originY - y;
            const width = Math.abs(dx);
            const height = Math.abs(dy);
            Dispatcher.dispatch({
              type: 'ANNOTATIONS_UPDATE_LAST',
              update: { x: dx > 0 ? x : last[1].originX, y: dy > 0 ? y : last[1].originY, width: width.toString(), height: height.toString() },
            });
          } else {
            // 회전 상태: 화면(회전된 뷰) 축에 정렬되도록 transform 기반으로 그린다.
            const ox = parseFloat(last[1].originX);
            const oy = parseFloat(last[1].originY);
            const { scaleX, scaleY } = getAspectScale(Store);
            const { dlx, dly } = userDeltaToLocal(x, y, scaleX, scaleY, theta, ox, oy);
            const localX = Math.min(0, dlx);
            const localY = Math.min(0, dly);
            const width = Math.abs(dlx);
            const height = Math.abs(dly);
            Dispatcher.dispatch({
              type: 'ANNOTATIONS_UPDATE_LAST',
              update: {
                x: localX.toString(),
                y: localY.toString(),
                width: width.toString(),
                height: height.toString(),
                transform: `translate(${ox}, ${oy}) scale(${scaleX}, ${scaleY}) rotate(${-theta})`,
                drawRotation: theta.toString(),
              },
            });
          }
        }
      }
      break;

    case 'DRAWELLIPSE':
      if (Store.isActivityInProgress()) {
        const last = Store.getLast();
        if (last && last[0] === 'ellipse') {
          const theta = Store.getRotation() || 0;
          // 시작 지점(누름 위치)을 타원 중앙이 아니라 경계(바운딩 박스 모서리)로 사용한다.
          // 즉 누름 위치와 현재 위치를 대각 모서리로 하는 박스에 내접하는 타원을 그린다.
          const ox = parseFloat(last[1].originX);
          const oy = parseFloat(last[1].originY);
          if (!theta) {
            // 회전 없음: 이미지 축 정렬 바운딩 박스
            const cx = (ox + x) / 2;
            const cy = (oy + y) / 2;
            const rx = Math.abs(x - ox) / 2;
            const ry = Math.abs(y - oy) / 2;
            Dispatcher.dispatch({
              type: 'ANNOTATIONS_UPDATE_LAST',
              update: { cx: cx.toString(), cy: cy.toString(), rx: rx.toString(), ry: ry.toString() },
            });
          } else {
            // 회전 상태: 화면 축에 정렬되도록 transform 기반으로 그린다.
            const { scaleX, scaleY } = getAspectScale(Store);
            const { dlx, dly } = userDeltaToLocal(x, y, scaleX, scaleY, theta, ox, oy);
            const rx = Math.abs(dlx) / 2;
            const ry = Math.abs(dly) / 2;
            Dispatcher.dispatch({
              type: 'ANNOTATIONS_UPDATE_LAST',
              update: {
                cx: (dlx / 2).toString(),
                cy: (dly / 2).toString(),
                rx: rx.toString(),
                ry: ry.toString(),
                transform: `translate(${ox}, ${oy}) scale(${scaleX}, ${scaleY}) rotate(${-theta})`,
                drawRotation: theta.toString(),
              },
            });
          }
        }
      }
      break;

    case 'RULER':
      if (Store.isActivityInProgress()) {
        const last = Store.getLast();
        if (last && last[0] === 'line') {
          const x2 = x;
          const y2 = y;
          Dispatcher.dispatch({
            type: 'ANNOTATIONS_UPDATE_LAST',
            update: { x2: x2.toString(), y2: y2.toString() },
          });
        }
      }
      break;

    case 'DRAWARROW':
      if (Store.isActivityInProgress()) {
        const last = Store.getLast();
        if (last && last[0] === 'g') {
          const x2 = x;
          const y2 = y;
          const lineEl = last[2];
          const x1 = parseFloat(lineEl[1].x1);
          const y1 = parseFloat(lineEl[1].y1);
          const zoomLevel = Store.getZoomLevel() || 1;
          const width = Store.getWidth() || 1;
          const height = Store.getHeight() || 1;
          const aspectRatio = width / height;
          const arrowHead = getArrowHeadGeometry({
            x1,
            y1,
            x2,
            y2,
            zoomLevel,
            aspectRatio,
          });

          last[1].arrowEndX = x2.toString();
          last[1].arrowEndY = y2.toString();

          lineEl[1].x2 = arrowHead.lineEndX.toString();
          lineEl[1].y2 = arrowHead.lineEndY.toString();

          const pathEl = last[3];
          pathEl[1].d = arrowHead.pathD;

          Dispatcher.dispatch({
            type: 'ANNOTATIONS_UPDATE_LAST',
            update: last,
          });
        }
      }
      break;

    default:
      break;
  }
}
